import { remainingRecordingMs } from "@msuite/utils/videos";
import { useEffect, useState } from "react";
import { getElapsedMs, useRecorderStore } from "./recorder-session";

/** `MM:SS`, growing an hours field only once there is one — a tutorial rarely needs it. */
export function formatElapsed(totalMs: number) {
	const totalSeconds = Math.floor(Math.max(0, totalMs) / 1000);
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;
	const parts = hours > 0 ? [hours, minutes, seconds] : [minutes, seconds];
	return parts.map((part) => String(part).padStart(2, "0")).join(":");
}

/**
 * The running time of the current recording as `HH:MM:SS`.
 *
 * The value is recomputed from timestamps on every render, and the interval only asks for a
 * re-render. That distinction is the whole point: a hidden tab clamps timers, so a counter that
 * accumulated ticks would fall behind exactly while the author works in another application,
 * then jump when they come back. Recomputing means a late tick still shows the right time.
 *
 * Two consequences are handled here. Coming back to a hidden tab, the next clamped tick may be
 * up to a second away, so the clock would paint stale for a moment after the author looks at it
 * again — `visibilitychange` asks for the repaint immediately. And while paused the reading is
 * frozen by definition, so the interval stops rather than re-rendering once a second for a
 * string that cannot change.
 *
 * The remaining time rides along on the same tick. A recording stops on its own at the length
 * limit, and a stop nobody saw coming reads as a crash — so the panel is given what it needs to
 * warn before it happens.
 *
 * @returns The formatted elapsed time and how much recording time is left, correct from the
 * first frame.
 */
export function useRecordingElapsed() {
	/** Store */
	const status = useRecorderStore((state) => state.status);

	/** State */
	const [, setTick] = useState(0);

	/** Effects */
	useEffect(() => {
		if (status !== "recording") return;
		const repaint = () => setTick((value) => value + 1);
		const interval = setInterval(repaint, 1000);
		const onVisible = () => {
			if (document.visibilityState === "visible") repaint();
		};
		document.addEventListener("visibilitychange", onVisible);
		return () => {
			clearInterval(interval);
			document.removeEventListener("visibilitychange", onVisible);
		};
	}, [status]);

	/** Return */
	const elapsedMs = getElapsedMs();
	const remainingMs = remainingRecordingMs(elapsedMs);
	return { elapsed: formatElapsed(elapsedMs), remaining: formatElapsed(remainingMs), remainingMs };
}
